import PropTypes from 'prop-types';
import { Component } from 'react';
import Modal from 'react-modal';

import { selectUserRolePermissions } from '@socialive/common';
import { connect } from 'react-redux';
import { routerHistoryShape, routerLocationShape } from '../helpers/propTypeShapes';

import AccountNav from '../components/AccountNav';
import ConfirmationModal from '../components/ConfirmationModal';
import CustomTable, { PlayerTableRowRenderer } from '../components/CustomTable';
import dynamicallySort from '../helpers/dynamicallySort';

import '../style/players.css';

class Players extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalIsOpen: false,
      selectedPlayers: [],
      sortedPlayers: [],
      sortElement: 'name',
      sortDirection: 'asc',
      wasDataFetched: false,
      tableHeaderItems: [
        { sort: 'name', displayName: 'PLAYER NAME' },
        { sort: 'description', displayName: 'DESCRIPTION', disableSorting: true },
        { sort: 'userName', displayName: 'UPDATED BY' },
      ],
    };

    this.closeModal = this.closeModal.bind(this);
    this.handlePlayerCreate = this.handlePlayerCreate.bind(this);
    this.handlePlayerNameClick = this.handlePlayerNameClick.bind(this);
    this.handlePlayersDelete = this.handlePlayersDelete.bind(this);
    this.handlePlayersDeleteRequest = this.handlePlayersDeleteRequest.bind(this);
    this.handlePlayerSelect = this.handlePlayerSelect.bind(this);
    this.handlePlayersSort = this.handlePlayersSort.bind(this);
    this.handleSelectAllPlayers = this.handleSelectAllPlayers.bind(this);
    this.openModal = this.openModal.bind(this);
    Modal.setAppElement('body');
  }

  componentDidMount() {
    const authorized = this.props.checkAuthorization('players');
    if (authorized) {
      this.getPlayerList();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.canManagePlayers) {
      this.props.history.push('/dashboard');
    } else if (prevProps.accountId !== this.props.accountId) {
      this.getPlayerList();
    }
  }

  handlePlayerCreate() {
    const defaultPlayerName = 'New Player';
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        Authorization: sessionStorage.getItem('authToken'),
      },
      body: JSON.stringify({
        name: defaultPlayerName,
        description: '',
        account: `accounts/${this.props.accountId}/`,
      }),
    };
    let id = '';

    fetch(`${this.props.APIBaseURL}/players`, params)
      .then((response) => {
        if (response.status === 401) {
          // eslint-disable-next-line no-console
          console.error('Received 401 response from handlePlayerCreate. Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then((data) => {
        // eslint-disable-next-line no-underscore-dangle
        [, id] = data._links.self.href.split('players/');
        if (window.opener) {
          window.opener.postMessage(
            {
              message: 'playerCreate',
              playerData: {
                id,
              },
            },
            '*'
          );
        }
        if (window.webcasterWindow) {
          window.webcasterWindow.postMessage(
            {
              message: 'playerCreate',
              playerData: {
                id,
              },
            },
            '*'
          );
        }
        this.props.history.push(`/player/${id}`);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('player create API call failed. ', error);
      });
  }

  /**
   * Callback to handle onClick events
   *
   * @param {string} playerId - id of the selected player.
   * @param {string} playerHash - hash of the selected player.
   * @returns void
   */
  handlePlayerNameClick(playerId, playerHash) {
    this.props.history.push({
      pathname: `/player/${playerId}`,
      state: { playerHash },
    });
  }

  handlePlayersDelete() {
    this.state.selectedPlayers.forEach((player) => {
      const params = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-API-VERSION': '2.0',
          Authorization: sessionStorage.getItem('authToken'),
        },
      };

      fetch(`${this.props.APIBaseURL}/players/${player}`, params)
        .then((response) => {
          if (response.status === 401) {
            this.props.trackEvent('401', {
              source: 'handlePlayersDelete',
            });
            // eslint-disable-next-line no-console
            console.error('Received 401 response from handlePlayersDelete (Players). Redirecting to login.');
            this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          }
        })
        .then(() => {
          if (window.opener) {
            window.opener.postMessage(
              {
                message: 'playerDelete',
                playerData: {
                  id: player,
                },
              },
              '*'
            );
          }
          if (window.webcasterWindow) {
            window.webcasterWindow.postMessage(
              {
                message: 'playerDelete',
                playerData: {
                  id: player,
                },
              },
              '*'
            );
          }
        })
        .then(() => {
          this.setState(
            {
              selectedPlayers: [],
            },
            () => {
              this.closeModal();
              this.getPlayerList();
            }
          );
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('player delete API call failed. ', error);
        });
    });
  }

  handlePlayersDeleteRequest() {
    this.openModal();
  }

  /**
   * Callback to handle single player checkbox check/uncheck
   *
   * @param {string} playerId - id of the selected player.
   * @param {boolean} isChecked - current status of the checkbox.
   * @returns void
   */
  handlePlayerSelect(playerId, isChecked) {
    const { selectedPlayers, sortedPlayers } = this.state;

    const newSortedPlayers = sortedPlayers.map((player) => {
      if (player.id === playerId) {
        return { ...player, selected: isChecked };
      }
      return player;
    });

    const newSelectedPlayers = isChecked
      ? [...selectedPlayers, playerId]
      : selectedPlayers.filter((selectedPlayerId) => selectedPlayerId !== playerId);

    this.setState({
      sortedPlayers: newSortedPlayers,
      selectedPlayers: newSelectedPlayers,
    });
  }

  /**
   * Callback to handle sorting by column
   *
   * @param {string} sortBy - property to sort by.
   * @returns void
   */
  handlePlayersSort(sortBy) {
    const { sortedPlayers, sortElement, sortDirection } = this.state;
    const sortingDirection = sortBy === sortElement && sortDirection === 'asc' ? 'desc' : 'asc';

    const newSort = sortedPlayers.sort(dynamicallySort(sortBy, sortingDirection));

    this.setState({
      sortedPlayers: newSort,
      sortDirection: sortingDirection,
      sortElement: sortBy,
    });
  }

  /**
   * Callback to handle sorting by column
   *
   * @param {string} sortBy - property to sort by.
   * @returns void
   */
  handleSelectAllPlayers(isChecked) {
    const { sortedPlayers } = this.state;
    const newSortedPlayers = sortedPlayers.map((user) => ({ ...user, selected: isChecked }));

    const selectedPlayers = isChecked ? sortedPlayers.map(({ id }) => id) : [];

    this.setState({
      sortedPlayers: newSortedPlayers,
      selectedPlayers,
    });
  }

  getPlayerList() {
    const params = {
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        Authorization: sessionStorage.getItem('authToken'),
      },
    };

    fetch(`${this.props.APIBaseURL}/players/search/by-account?id=${this.props.accountId}&projection=list`, params)
      .then((response) => {
        if (response.status === 401) {
          // eslint-disable-next-line no-console
          console.error('Received 401 response from getPlayerList. Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then((data) => {
        this.processPlayersList(data, true);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching player list. ', error);
      });
  }

  openModal() {
    if (this.state.selectedPlayers.length > 1) {
      this.setState(
        {
          pluralPlayers: 'these players',
        },
        () => {
          this.setState({ modalIsOpen: true });
        }
      );
    } else {
      this.setState(
        {
          pluralPlayers: 'this player',
        },
        () => {
          this.setState({ modalIsOpen: true });
        }
      );
    }
  }

  closeModal() {
    this.setState({ modalIsOpen: false });
  }

  /**

	 * Process the players data coming from the API. Formatting and Sorting the channel list
	 *
	 * @param {object} data - players to edit.
	 * @returns void
	 */
  processPlayersList(data) {
    // eslint-disable-next-line no-underscore-dangle
    if (data?._embedded?.players?.length <= 0) {
      return;
    }

    const { sortElement, sortDirection } = this.state;
    // eslint-disable-next-line no-underscore-dangle
    const sortedPlayers = data._embedded.players.sort(dynamicallySort(sortElement, sortDirection)).map((player) => ({
      ...player,
      selected: false,
      // eslint-disable-next-line no-underscore-dangle
      id: this.props.getPrimaryKey(player._links.self),
    }));

    this.setState({
      sortedPlayers,
      wasDataFetched: true,
    });
  }

  render() {
    const { wasDataFetched, selectedPlayers, sortedPlayers } = this.state;
    const isAllChecked = sortedPlayers.length === selectedPlayers.length && sortedPlayers.length > 0;

    return (
      <div className="players-content content-container">
        <AccountNav />
        <div className="players-header header-container">
          <h2 className="players-header-title">Players</h2>
          <span className="subheader">Embed live broadcasts on webpages</span>
          <div className="players-header-buttons">
            {selectedPlayers.length > 0 && (
              <button
                type="button"
                id="playersDeleteBtn"
                className="btn white btn-players-delete active"
                onClick={this.handlePlayersDeleteRequest}
              >
                Delete
              </button>
            )}
            <button type="button" className="btn btn-players-create" onClick={this.handlePlayerCreate}>
              New Player
            </button>
          </div>
        </div>

        {wasDataFetched && sortedPlayers.length === 0 && (
          <div className="no-players-msg">
            <span>
              You do not have any Players created. Click the &quot;New Player&quot; button to create one, or{' '}
              <a href="https://go.socialive.us/support-center">click here</a> to learn more about Socialive Players.
            </span>
          </div>
        )}

        {wasDataFetched && sortedPlayers.length > 0 && (
          <div className="player-custom-table">
            <CustomTable
              handleSort={this.handlePlayersSort}
              handleSelectAll={this.handleSelectAllPlayers}
              headerItems={this.state.tableHeaderItems}
              selectedHeaderItem={this.state.sortElement}
              sortDirection={this.state.sortDirection}
              checkAll={isAllChecked}
              tableBody={sortedPlayers.map((item) => (
                <PlayerTableRowRenderer
                  key={item.id}
                  handleItemClicked={this.handlePlayerNameClick}
                  handleSelectItem={this.handlePlayerSelect}
                  item={item}
                />
              ))}
            />
          </div>
        )}

        <ConfirmationModal
          isOpen={this.state.modalIsOpen}
          handleOnClose={() => this.closeModal()}
          handleOnConfirm={this.handlePlayersDelete}
          resourceDescription={`Do you really want to delete ${this.state.pluralPlayers}? This cannot be undone.`}
        />
      </div>
    );
  }
}

Players.propTypes = {
  accountId: PropTypes.string.isRequired,
  APIBaseURL: PropTypes.string.isRequired,
  checkAuthorization: PropTypes.func.isRequired,
  getPrimaryKey: PropTypes.func.isRequired,
  history: routerHistoryShape.isRequired,
  location: routerLocationShape.isRequired,
  reAuth: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  canManagePlayers: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  const { canManagePlayers } = selectUserRolePermissions(state);
  return { canManagePlayers };
};

export default connect(mapStateToProps)(Players);
